import emoji from 'node-emoji';
import { apiEvents } from 'features/pay/constants';
import { logout } from 'actions';
import * as actionsTypes from '../actions/types';
import Sentry from '../utils/sentry';
import { VERIFY_PASSCODE_FAILURE } from '../actions/types';
import { isWeb } from '../constants';
// NB: by default we hide the modal for all GET requests.
const ERRORS_HIDE_MODAL = [
    actionsTypes.ON_REPORT_FINISHED_FAILURE,
    actionsTypes.ON_REPORT_OPENED_FAILURE,
    actionsTypes.ON_REPORT_SWIPED_FAILURE,
    actionsTypes.POST_ANSWER_FAILURE,
    actionsTypes.SET_APPSFLYER_FAILURE,
    actionsTypes.SET_PUSH_FAILURE,
    actionsTypes.LINK_NUMBER_VERIFY_CODE_FAILURE,
    actionsTypes.VERIFY_CODE_FAILURE,
    apiEvents.paymentSend[2],
    apiEvents.paymentRequestFulfill[2],
];
// Only the real token endpoint may end the session. Match the pathname exactly
// so a resource path that merely contains "/oauth/token" can't trigger logout.
const isTokenEndpoint = (url) => {
    if (!url) {
        return false;
    }
    try {
        return new URL(url).pathname === '/oauth/token';
    }
    catch {
        return false;
    }
};
// TODO: Provide more precise types
export const errorMiddleware = () => (next) => async (action) => {
    if (action && action.errorType === 'ApiError') {
        const { headers, ok, status, statusText, url } = action.payload;
        const { request, type } = action;
        const errorParams = {
            apiPayload: { headers, ok, status, statusText, type, url },
            type: action.type,
            errorName: undefined,
            error: 'Error',
            extra: action.extra,
            errorMessage: 'This is awkward, please try again.',
            errorType: 'ApiError',
            errorDisplayModal: (action.method !== 'GET' &&
                !ERRORS_HIDE_MODAL.includes(action.type) &&
                !action.errorHideModal &&
                !action.muteAlert &&
                !action.muteAlertForStatuses?.includes(status)) ||
                action.muteAlert === false,
        };
        try {
            errorParams.apiPayload.fullResponse = await action.payload.json();
        }
        catch {
            // noop
        }
        const message = errorParams.apiPayload.fullResponse;
        switch (status) {
            case 400: // expired access token or BadRequestError
                if (message?.user_message) {
                    errorParams.errorMessage = message.user_message;
                }
                else if (message?.userMessage) {
                    errorParams.errorMessage = message.userMessage;
                }
                break;
            case 401: // expired access token
                break;
            case 403:
                // Only a 403 from the token endpoint (an invalid/expired refresh
                // token) ends the session. A 403 from any other resource endpoint is
                // a permission error (e.g. ForbiddenError) and must NOT log the user
                // out.
                if (isTokenEndpoint(url) &&
                    message?.error_description === 'Invalid refresh token') {
                    errorParams.error = 'Session expired';
                    errorParams.errorMessage = emoji.emojify('Your session has expired. Please login again. :pray:');
                    errorParams.errorType = 'SessionExpired';
                    errorParams.requestTimestamp =
                        headers?.requestTimestamp || String(new Date().getTime());
                    errorParams.errorDisplayModal = true;
                }
                else if (message?.error === 'server_error') {
                    errorParams.errorMessage = message.error_description;
                    errorParams.errorDisplayModal = true;
                }
                else {
                    if (message?.error?.name === 'ForbiddenError') {
                        errorParams.errorType = 'ForbiddenError';
                    }
                    if (message?.user_message) {
                        errorParams.errorMessage = message.user_message;
                    }
                    else if (message?.userMessage) {
                        errorParams.errorMessage = message.userMessage;
                    }
                }
                break;
            case 404:
                if (message?.error?.name === 'SubscriptionNotFoundError') {
                    errorParams.error = 'Please wait';
                    errorParams.errorMessage = message.error.userMessage;
                }
                else if (message?.error?.name === 'UserNotFoundError') {
                    errorParams.error = 'User not found';
                    errorParams.errorMessage =
                        'Please go back and sign up if you don’t already have an account.';
                }
                else if (message?.error?.name === 'SpaceNotFoundError') {
                    errorParams.error = 'Space not found';
                    errorParams.errorMessage =
                        'This space is deleted, or the owner removed you from the space. Please contact them for more information.';
                }
                else if (message?.user_message) {
                    errorParams.errorMessage = message.user_message;
                }
                else if (message?.userMessage) {
                    errorParams.errorMessage = message.userMessage;
                }
                break;
            case 409:
            case 422:
            case 500:
            case 502:
                if (message) {
                    errorParams.errorMessage = message.user_message;
                    errorParams.errorName = message.error?.name;
                    // Show a server-provided title when present (set via setUserTitle on
                    // the backend error); otherwise keep the generic 'Error' title.
                    if (message.error?.userTitle) {
                        errorParams.error = message.error.userTitle;
                    }
                    // Oauth endpoint returns errors in the format:
                    // {
                    //   "error": "invalid_grant",
                    //   "error_description": "Verification code is incorrect"
                    // }
                    if (errorParams.errorMessage === undefined) {
                        errorParams.errorMessage = message.error_description;
                        errorParams.errorName = message.error;
                    }
                }
                break;
            case 503:
                errorParams.errorType = 'ServerDown';
                errorParams.errorDisplayModal = false;
                if (message) {
                    errorParams.errorMessage = message.user_message;
                }
                break;
            default:
                break;
        }
        Sentry.logNetworkError({
            url,
            method: action.method,
            status,
            error: errorParams.error,
            errorMessage: errorParams.errorMessage,
            requestId: request || type,
        });
        return next(errorParams);
    }
    if (action?.errorType === 'SetError' ||
        action?.errorType === 'SessionExpired') {
        if ((action?.type === VERIFY_PASSCODE_FAILURE ||
            isWeb ||
            action.muteAlertForToken) &&
            action?.errorType === 'SessionExpired') {
            return next(logout());
        }
        return next({
            type: action.type,
            extra: action.extra,
            error: action.error || 'Error',
            requestTimestamp: action?.requestTimestamp,
            errorMessage: action.errorMessage || 'This is awkward, please try again.',
            errorType: action.errorType,
            errorDisplayModal: !ERRORS_HIDE_MODAL.includes(action.type),
        });
    }
    return next(action);
};
